// src/components/BatchReports/BatchReportSubmitSection.js
import React, { useMemo, useState } from "react";
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DynamicParameterSelector from "components/DynamicReportInputs/DynamicParameterSelector";
import { makeStyles } from "@material-ui/core/styles";

import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import InputAdornment from "@material-ui/core/InputAdornment";
import DescriptionIcon from "@material-ui/icons/Description";
import PublishIcon from "@material-ui/icons/Publish";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { dbGet, dbPostWithFormData } from "utils/DBFetchers";
import { th } from "date-fns/locale";


const useStyles = makeStyles((theme) => ({
  accordion: {
    background: "#eef3fb",              // ✅ “solo lectura”: azul/gris suave
    border: "1px solid #cfd9ea",
    borderRadius: 8,
    boxShadow: "none",
    overflow: "hidden",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
  },
  summary: {
    padding: "0 16px",
    minHeight: 56,
    "&.Mui-expanded": { minHeight: 56 },
  },
  summaryContent: {
    margin: "12px 0",
    "&.Mui-expanded": { margin: "12px 0" },
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    color: "#3f51b5",                   // usa color “primary” visualmente
    opacity: 0.9,
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    marginTop: 2,
  },
  details: {
    padding: "0 16px 16px 16px",
    display: "block",
  },
  iframeContainer: {
    border: "1px solid #cfd9ea",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",                 // ✅ el visor en blanco para que el PDF se lea bien
    height: 520,
  },
  docActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 10,
  },
  docActionBtn: {
    textTransform: "none",
    borderRadius: 8,
  },


  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  headerActionBtn: {
    textTransform: "none",
    fontSize: 13,
    padding: "4px 8px",
  },

  fileInputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  generalPurposeField: {
    flexGrow: 1,
    backgroundColor: "#fff",
  },
  selectionField: {
    whiteSpace: "nowrap",
    height: 54,
  },
}));



function normCategory(cat) {
  return (cat ?? "").toString().trim().toLowerCase();
}

function displayCategory(catNorm) {
  return catNorm ? catNorm : "sin categoría";
}

function normAlias(alias) {
  return (alias ?? "").toString().trim().toLowerCase();
}

export default function BatchReportSubmitSection({
  tasksByKey,
}) {
  // --- UI state local a la sección ---
  const [selectedCategoryNorm, setSelectedCategoryNorm] = useState("");
  const [selectedTaskKey, setSelectedTaskKey] = useState("");
  const [dynamicParams, setDynamicParams] = useState({});
  const [inputDescription, setInputDescription] = useState("");
  const [isDocOpen, setIsDocOpen] = useState(false);
  const hiddenFileInput = React.useRef(null);
  const [selectedFile, setSelectedFile] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState(null);

  const classes = useStyles();

  const { categories, tasksByCategory } = useMemo(() => {
    const entries = Object.entries(tasksByKey || {});

    // 1) armamos items normalizados para agrupar
    const items = entries.map(([cmdName, cfg]) => {
      const catNorm = normCategory(cfg?.category);
      return {
        cmdName,
        cfg,
        catNorm,
        aliasNorm: normAlias(cfg?.alias) || cmdName.toLowerCase(),
      };
    });

    // 2) group by category_norm
    const map = new Map();
    for (const it of items) {
      const key = it.catNorm;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }

    // 3) sort categories
    const cats = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));

    // 4) sort tasks within category by alias (fallback cmdName)
    for (const cat of cats) {
      map.get(cat).sort((a, b) => a.aliasNorm.localeCompare(b.aliasNorm));
    }

    return {
      categories: cats,
      tasksByCategory: map,
    };
  }, [tasksByKey]);

  // Autoselección de primera categoría si no hay elegida
  React.useEffect(() => {
    if (!selectedCategoryNorm && categories.length > 0) {
      setSelectedCategoryNorm(categories[0]); // puede ser "" (sin categoría)
    }
  }, [categories, selectedCategoryNorm]);

  const tasksForSelectedCategory = useMemo(() => {
    return tasksByCategory.get(selectedCategoryNorm) || [];
  }, [tasksByCategory, selectedCategoryNorm]);

  const selectedTaskConfig = selectedTaskKey ? tasksByKey?.[selectedTaskKey] : null;

  const docUrlRaw = selectedTaskConfig?.docx_documentation_url ?? "";
  const docUrl = (typeof docUrlRaw === "string" ? docUrlRaw.trim() : "");
  const hasDoc = Boolean(docUrl);

  const officeEmbedUrl = (rawUrl) => {
    const encoded = encodeURIComponent(rawUrl);
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
  };

  const iframeSrc = hasDoc ? officeEmbedUrl(docUrl) : "";


  const handleCategoryChange = (event) => {
    const newCat = event.target.value;
    setSelectedCategoryNorm(newCat);
    setSelectedTaskKey("");
    setDynamicParams({});
  };

  const handleTaskChange = (event) => {
    const newTaskKey = event.target.value;
    setSelectedTaskKey(newTaskKey);
    setSelectedFile(undefined);
    setDynamicParams({});
    setIsDocOpen(false);
    setResultMessage(null);
  };

  const handleParamChange = (key, value) => {
    setDynamicParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDescriptionChange = (event) => {
    setInputDescription(event.target.value);
  };

  const handleSelectFileClick = () => {
    hiddenFileInput.current.click();
  };

  const selectFile = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleExecute = async () => {
    if (!selectedTaskKey) return;

    setIsSubmitting(true);
    setResultMessage(null);

    try {
      const formData = new FormData();
      const missingParamsError = new Error(`Faltan completar parámetros obligatorios`)

      if (inputDescription) {
        formData.append("input_description", inputDescription);
      }
      // debugger;
      if (selectedTaskConfig?.input_file_definition?.enable_input_file && selectedFile) {
        if (selectedFile) {
          formData.append("input_file", selectedFile);
        } else {
          throw missingParamsError;
        }
      }
      
      selectedTaskConfig?.mandatory_selectors?.forEach((key) => {
        const value = dynamicParams[key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        } else {
          throw missingParamsError;
        }
      });

      await dbPostWithFormData(`cli/batch/${selectedTaskKey}`, formData);
      setResultMessage({ type: "success", text: "¡Tarea iniciada exitosamente!" });
      
      setDynamicParams({});
      setInputDescription("");
      setSelectedFile(undefined);

    } catch (error) {
      console.error(error?.message || "Unknown error");
      const errorMsg = error.response?.data?.error || error.message || "Error al iniciar la tarea.";
      setResultMessage({ type: "error", text: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAcceptedExtensions = () => {
    const types = [];
    if (selectedTaskConfig?.input_file_definition?.type_xlsx) types.push(".xlsx");
    if (selectedTaskConfig?.input_file_definition?.type_csv) types.push(".csv");
    return types.join(",");
  };

  // Orden de selectores: mandatory (en su orden) y luego optional (en su orden)
  const requiredSelectors = selectedTaskConfig?.mandatory_selectors || [];
  const optionalSelectors = selectedTaskConfig?.optional_selectors || [];

  return (
    <Grid container direction="column" spacing={3} style={{ padding: 16, background: "#f5f5f5" }}>
      <Grid item>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
          Crear nueva tarea
        </Typography>
        <Divider />
      </Grid>

      {/* Categoría */}
      <Grid item>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
          Categoría
        </Typography>

        <FormControl variant="outlined" fullWidth>
          <InputLabel>Seleccione una categoría</InputLabel>
          <Select
            value={selectedCategoryNorm}
            onChange={handleCategoryChange}
            label="Seleccione una categoría"
          >
            {categories.map((catNorm) => (
              <MenuItem key={catNorm || "__uncategorized__"} value={catNorm}>
                {displayCategory(catNorm)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Tarea */}
      <Grid item>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
          Selección de tarea
        </Typography>

        <FormControl variant="outlined" fullWidth disabled={tasksForSelectedCategory.length === 0}>
          <InputLabel>Seleccione una tarea</InputLabel>
          <Select
            value={selectedTaskKey}
            onChange={handleTaskChange}
            label="Seleccione una tarea"
          >
            <MenuItem value="">
              <em>Ninguno</em>
            </MenuItem>

            {tasksForSelectedCategory.map(({ cmdName, cfg }) => (
              <MenuItem key={cmdName} value={cmdName}>
                {cfg?.alias || cmdName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Descripción */}
      {selectedTaskConfig?.description ? (
        <Grid item>
          <Typography variant="body1" style={{ fontStyle: "italic", color: "#555", marginTop: -10 }}>
            {selectedTaskConfig.description}
          </Typography>
        </Grid>
      ) : null}

      {/* Documentación (PDF) */}
      {selectedTaskConfig && hasDoc ? (
        <Grid item>
          <Accordion
            className={classes.accordion}
            expanded={isDocOpen}
            onChange={(_, expanded) => setIsDocOpen(expanded)}
          >
            <AccordionSummary
              className={classes.summary}
              classes={{ content: classes.summaryContent }}
              expandIcon={<ExpandMoreIcon />}
            >
              <div className={classes.headerContainer}>
                {/* Lado izquierdo */}
                <div className={classes.headerLeft}>
                  <Tooltip title="Visualización (solo lectura)">
                    <DescriptionOutlinedIcon className={classes.headerIcon} />
                  </Tooltip>

                  <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                    Documentación
                  </Typography>
                </div>

                {/* Lado derecho */}
                <div className={classes.headerActions}>
                  <Button
                    size="small"
                    variant="text"
                    className={classes.headerActionBtn}
                    startIcon={<OpenInNewIcon />}
                    href={iframeSrc}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Abrir en nueva pestaña
                  </Button>
                </div>
              </div>
            </AccordionSummary>

            <AccordionDetails className={classes.details}>
              <div className={classes.iframeContainer}>
                <iframe
                  title="Tarea - documentación"
                  src={iframeSrc}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ) : null}

      {selectedTaskConfig && !hasDoc ? (
        <Grid item>
          <Typography variant="body2" color="textSecondary">
            Esta tarea no tiene documentación cargada.
          </Typography>
        </Grid>
      ) : null}



      {/* Input file condicional */}
      {selectedTaskConfig?.input_file_definition?.enable_input_file ? (
        <Grid item>
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
            Archivo de entrada
          </Typography>
          <div className={classes.fileInputContainer}>
            <TextField
              id="fileSelected"
              className={classes.generalPurposeField}
              disabled
              value={selectedFile ? selectedFile.name : ""}
              variant="outlined"
              placeholder="Ningún archivo seleccionado"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              className={classes.selectionField}
              variant="contained"
              color="primary"
              startIcon={<PublishIcon />}
              onClick={handleSelectFileClick}
            >
              Seleccionar Archivo
            </Button>
            {/* Input oculto real */}
            <input
              type="file"
              onChange={selectFile}
              style={{ display: "none" }}
              ref={hiddenFileInput}
              accept={getAcceptedExtensions()} 
            />
          </div>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
            Formatos aceptados: {getAcceptedExtensions().replace(/,/g, ", ") || "Todos"}
          </Typography>
        </Grid>
      ) : null}

      {/* Parámetros requeridos */}
      {selectedTaskConfig ? (
        <Grid item>
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
            Parámetros requeridos
          </Typography>

          <DynamicParameterSelector
            parameters={dynamicParams}
            onParameterChange={handleParamChange}
            requiredSelectors={requiredSelectors}
          />
        </Grid>
      ) : null}

      {/* Parámetros opcionales (lo dejo ya armado para el próximo paso) */}
      {selectedTaskConfig && optionalSelectors.length > 0 ? (
        <Grid item>
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
            Parámetros opcionales
          </Typography>

          <DynamicParameterSelector
            parameters={dynamicParams}
            onParameterChange={handleParamChange}
            requiredSelectors={optionalSelectors}
          />
        </Grid>
      ) : null}

      {/* Comentarios + botón ejecutar */}
      {selectedTaskConfig && (
        <>
          <Grid item>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
              Comentarios de tarea
            </Typography>
            <TextField
              label="Comentarios (opcional)"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={inputDescription}
              onChange={handleDescriptionChange}
              placeholder="Ingrese una descripción para identificar esta ejecución..."
              className={classes.generalPurposeField}
            />
          </Grid>

          <Grid item>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
              Ejecutar
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
              <Button
                variant="contained"
                color="primary"
                className={classes.executeButton}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleExecute}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Ejecutando..." : "Ejecutar Tarea"}
              </Button>

              {/* Mensaje de Resultado */}
              {resultMessage && (
                <Typography 
                  variant="body2" 
                  style={{ 
                    color: resultMessage.type === "success" ? "#2e7d32" : "#d32f2f",
                    fontWeight: 500,
                    padding: "8px 12px",
                    background: resultMessage.type === "success" ? null : "#ffebee",
                    borderRadius: 4,
                    width: "100%",
                  }}
                >
                  {resultMessage.text}
                </Typography>
              )}
            </div>
          </Grid>
        </>
      )}
    </Grid>
  );
}
